// manifest.util.ts

// Minimal typing so TS is happy when using import.meta.env
interface ImportMetaEnvBag {
  [key: string]: string | undefined;
}

interface ImportMeta {
  readonly env: ImportMetaEnvBag;
}

const ALLOWED_PROTOCOLS = ['https:', 'http:'];
const ALLOWED_HOSTS = ['exactsciences.com', 'localhost', '127.0.0.1', 'bs-local.com'];

function isAllowedUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    if (!ALLOWED_PROTOCOLS.includes(parsed.protocol)) {
      return false;
    }
    return ALLOWED_HOSTS.some(allowed => parsed.hostname === allowed || parsed.hostname.endsWith(`.${allowed}`));
  } catch {
    return false;
  }
}

export function buildManifestUrl(host: string, region?: string): string | undefined {
  const filename = region ? `federation.manifest.${region}.json` : 'federation.manifest.json';
  const url = `${host}/${filename}`;
  if (!isAllowedUrl(url)) {
    console.error('Blocked manifest URL construction for disallowed host:', host);
    return undefined;
  }
  return url;
}

export const getMfeManifest = async (host: string, region?: string) => {
  const manifestUrl = buildManifestUrl(host, region);
  if (!manifestUrl) {
    return undefined;
  }
  return fetch(manifestUrl, { cache: 'no-store' })
    .then(response => response.json())
    .catch(() => console.error('Failed to load manifest.json'));
};

/**
 * Get value from env or accept injectable env, Picks a variable from:
 *   1) the provided env argument (highest priority),
 *   2) import.meta.env (fallback),
 *   3) otherwise undefined.
 */

export type EnvBag = Record<string, string | undefined>;

// Load CLI-provided runtime env (if present)
export async function loadRuntimeEnv(): Promise<EnvBag> {
  try {
    const r = await fetch('/__env.runtime.json', { cache: 'no-store' });
    if (!r.ok) return {};
    const json = await r.json();
    return json && typeof json === 'object' ? (json as EnvBag) : {};
  } catch {
    return {};
  }
}

function resolveEnvValue(key: string, env?: Record<string, string | undefined>): string | undefined {
  if (env && env[key] !== null && env[key] !== '') {
    return env[key]!;
  }
  // fallback to Vite-style env if present (or esbuild define if you injected it)
  return (import.meta as any)?.env?.[key];
}

export function buildManifestHost(env?: Record<string, string | undefined>): string {
  return resolveEnvValue('MANIFEST_HOST', env) ?? location.origin;
}

export function getRegion(env?: Record<string, string | undefined>): string | undefined {
  return resolveEnvValue('REGION', env);
}

/**
 * Builds a manifest overrides object from env variables and returns it as a JSON string.
 * Only variables that exist / are non-empty are included.
 * Example output: {"gppCoreMfe":"https://localhost:9902/remoteEntry.js", ...}
 */
export function buildManifestOverrides(env?: Record<string, string | undefined>): Record<string, string | undefined> {
  // map of ENV_VAR -> manifest key
  const manifestOverrideEnvArgs: Record<string, string> = {
    OVERRIDE_CORE_REMOTE: 'gpp-core-mfe',
    OVERRIDE_ORDERING_REMOTE: 'gpp-ordering-mfe',
    OVERRIDE_ORDERS_REMOTE: 'gpp-orders-mfe',
    OVERRIDE_SPECIMEN_RETRIEVAL_REMOTE: 'gpp-specimen-retrieval-mfe',
    OVERRIDE_ACCOUNT_REMOTE: 'gpp-account-mfe',
    OVERRIDE_ADMIN_REMOTE: 'gpp-admin-mfe'
  };

  const manifestOverrides: Record<string, string> = {};

  for (const [envKey, manifestKey] of Object.entries(manifestOverrideEnvArgs)) {
    const val = resolveEnvValue(envKey, env);
    if (val) {
      if (!isAllowedUrl(val)) {
        console.error(`Blocked override for ${manifestKey}: disallowed URL`, val);
        continue;
      }
      manifestOverrides[manifestKey] = val;
    }
  }

  // Optional debug log
  // eslint-disable-next-line no-console
  console.log('[manifest] overrides:', manifestOverrides);

  return manifestOverrides;
}
